import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ButtonBase,
  Chip,
  Collapse,
  makeStyles,
  TextField,
  Typography,
} from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";
import { Autocomplete } from "@material-ui/lab";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExtraHeaders from "./ExtraHeaders";
import { countAdvanced, formatTokens, FormErrors, FormState } from "./helpers";

import { AIProvider } from "@dashboard-v3/api";

interface Props {
  form: FormState;
  provider: AIProvider;
  errors: FormErrors;
  disabled: boolean;
  onChange: (update: Partial<FormState>) => void;
}

export default function AdvancedSettings(props: Props) {
  const { form, provider, errors, disabled, onChange } = props;
  const { t } = useTranslation("storage");
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const model = provider.models.find(item => item.id === form.model.trim());
  const customized = countAdvanced(form);
  const reasoningOptions = model?.reasoningOptions || [];
  const isTemperatureUnsupported = model?.temperature === false;
  const isTemperatureDisabled = disabled || isTemperatureUnsupported;
  const iconClass = open
    ? `${classes.expandIcon} ${classes.expanded}`
    : classes.expandIcon;
  const reasoningPlaceholder = provider.defaultReasoning
    ? t("externalAI.form.defaultValue", { value: provider.defaultReasoning })
    : t("externalAI.form.providerDefault");
  const maxOutputHelper = getLimitHelper(
    errors.maxOutputTokens,
    "externalAI.form.modelLimit",
    model?.outputTokens
  );
  const maxInputHelper = getLimitHelper(
    errors.maxInputTokens,
    "externalAI.form.contextWindow",
    model?.contextTokens
  );
  const temperatureHelper = getTemperatureHelper();
  const extraParamsHelper = errors.extraParams
    ? t(errors.extraParams)
    : t("externalAI.form.extraParamsHelper");

  function getLimitHelper(errorKey: string, limitKey: string, limit?: number) {
    if (errorKey) return t(errorKey);
    if (limit === undefined) return "";
    return t(limitKey, { amount: formatTokens(limit) });
  }

  function getTemperatureHelper() {
    if (errors.temperature) return t(errors.temperature);
    if (isTemperatureUnsupported) {
      return t("externalAI.form.temperatureUnsupported");
    }
    return "";
  }

  return (
    <div className={classes.container}>
      <ButtonBase
        className={classes.toggle}
        onClick={() => setOpen(prev => !prev)}
        data-testid="external-ai__advanced-toggle"
      >
        <ExpandMoreIcon className={iconClass} />
        <Typography variant="body2" className={classes.toggleTitle}>
          {t("externalAI.form.advanced")}
        </Typography>
        {customized > 0 && (
          <Chip
            size="small"
            className={classes.customized}
            label={t("externalAI.form.customized", { count: customized })}
          />
        )}
        <Typography
          variant="caption"
          color="textSecondary"
          className={classes.toggleHint}
        >
          {t("externalAI.form.advancedHint")}
        </Typography>
      </ButtonBase>
      <Collapse in={open}>
        <div className={classes.content}>
          <div className={classes.groupTitle}>
            <Typography variant="body2" className={classes.groupName}>
              {t("externalAI.form.modelParams")}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {t("externalAI.form.modelParamsNote")}
            </Typography>
          </div>
          <div className={classes.grid}>
            <TextField
              label={t("externalAI.form.maxOutputTokens")}
              type="number"
              variant="outlined"
              fullWidth
              disabled={disabled}
              value={form.maxOutputTokens}
              placeholder={t("externalAI.form.defaultValue", {
                value: provider.defaultMaxOutputTokens,
              })}
              error={Boolean(errors.maxOutputTokens)}
              helperText={maxOutputHelper}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: 1 }}
              onChange={e => onChange({ maxOutputTokens: e.target.value })}
            />
            <Autocomplete
              freeSolo
              options={reasoningOptions}
              value={form.reasoning}
              disabled={disabled}
              onChange={(_, value) => onChange({ reasoning: value || "" })}
              onInputChange={(_, value) => onChange({ reasoning: value })}
              renderInput={params => (
                <TextField
                  {...params}
                  label={t("externalAI.form.reasoning")}
                  placeholder={reasoningPlaceholder}
                  variant="outlined"
                  fullWidth
                  InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                />
              )}
            />
            <TextField
              label={t("externalAI.form.temperature")}
              type="number"
              variant="outlined"
              fullWidth
              disabled={isTemperatureDisabled}
              value={form.temperature}
              placeholder={t("externalAI.form.providerDefault")}
              error={Boolean(errors.temperature)}
              helperText={temperatureHelper}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: 0, step: 0.1 }}
              onChange={e => onChange({ temperature: e.target.value })}
            />
            <TextField
              label={t("externalAI.form.maxInputTokens")}
              type="number"
              variant="outlined"
              fullWidth
              disabled={disabled}
              value={form.maxInputTokens}
              placeholder={t("externalAI.form.optional")}
              error={Boolean(errors.maxInputTokens)}
              helperText={maxInputHelper}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: 1 }}
              onChange={e => onChange({ maxInputTokens: e.target.value })}
            />
          </div>
          <TextField
            label={t("externalAI.form.extraParams")}
            multiline
            minRows={3}
            variant="outlined"
            fullWidth
            disabled={disabled}
            value={form.extraParams}
            placeholder={'{ "top_p": 0.9 }'}
            error={Boolean(errors.extraParams)}
            helperText={extraParamsHelper}
            className={classes.extraParams}
            InputLabelProps={{ shrink: true }}
            InputProps={{ className: classes.mono }}
            onChange={e => onChange({ extraParams: e.target.value })}
          />
          <ExtraHeaders
            headers={form.headers}
            disabled={disabled}
            onChange={headers => onChange({ headers })}
          />
        </div>
      </Collapse>
    </div>
  );
}

const useStyles = makeStyles(theme => ({
  container: {
    marginTop: 20,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
  },
  toggle: {
    display: "flex",
    justifyContent: "flex-start",
    gap: 10,
    width: "100%",
    padding: "12px 14px",
    borderRadius: 6,
    textAlign: "left",
  },
  toggleTitle: { fontWeight: 500 },
  customized: {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    color: theme.palette.primary.main,
    fontWeight: 500,
  },
  toggleHint: { marginLeft: "auto" },
  expandIcon: {
    color: theme.palette.text.secondary,
    transform: "rotate(-90deg)",
    transition: "transform .2s",
  },
  expanded: { transform: "rotate(0deg)" },
  content: { padding: "6px 14px 16px" },
  groupTitle: {
    display: "flex",
    alignItems: "baseline",
    gap: 8,
    margin: "14px 0",
  },
  groupName: { fontWeight: 500 },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 16,
  },
  extraParams: { marginTop: 16 },
  mono: { fontFamily: "monospace", fontSize: 13 },
}));
